import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ChevronRight } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { SERVICE_GROUPS, SERVICES_FLAT, CONTACT, PRICING_DISCLAIMER, SERVICE_PHOTOS } from "../data/site";

const CLOSER_VARIANTS = [
  (s, g) =>
    `Every ${s.title.toLowerCase()} engagement is executed by trained Mobile Maintenance technicians working from a standardized field process, the same one delivered across ${g?.category.toLowerCase()} for fleets nationwide. Every asset leaves better than we found it. That's the line in the sand.`,  (s) =>
    `Behind every ${s.title.toLowerCase()} job is the same rigorously trained field team, the same documented playbook, and the same national footprint that supports fleets nationwide. Show up on time, do the work right, and communicate every step. That's the standard.`,
  (s) =>
    `This service runs on Mobile Maintenance's field discipline: certified technicians, a documented process, and reporting infrastructure that keeps fleets audit-ready. Whether it's a single ${s.title.toLowerCase()} visit or a recurring program, if it isn't documented, it didn't happen.`,
  (s) =>
    `When you book ${s.title.toLowerCase()}, you're tapping into two decades of field-tested process. Every visit is documented, every technician is trained on the same standard, and every record lands in one place. We measure ourselves by your uptime, not our invoices.`,
  (s) =>
    `One team, one standard, one system of record. That's how Mobile Maintenance delivers ${s.title.toLowerCase()} nationwide without the drift that comes from juggling multiple vendors. Safety of the crew and integrity of the equipment come before speed, always.`,
  (s) =>
    `${s.title} is delivered by trained technicians working from the same playbook that supports fleets nationwide. Same process, same documentation, whether you have five assets or five hundred. No surprises on the invoice and no gaps in the record.`,
  (s, g) =>
    `We treat every ${s.title.toLowerCase()} request the same way we treat the largest ${g?.category.toLowerCase()} contract on our books. Trained crew, documented workflow, and a single point of contact who knows your fleet. Our reputation lives on every jobsite we leave behind, and we treat it that way.`,
];

const pickCloserIndex = (slug) => {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return h % CLOSER_VARIANTS.length;
};

// 10 pricing-heading variants rotated across service pages so no two neighbours repeat.
const PRICING_HEADINGS = [
  ["No ", "hidden", " costs."],
  ["Plain and ", "simple", "."],
  ["By the ", "hour", "."],
  ["Fair and ", "firm", "."],
  ["No games, no ", "gotchas", "."],
  ["Rates, not ", "riddles", "."],
  ["What it ", "runs", "."],
  ["On the ", "level", "."],
  ["Straight-forward ", "rates", "."],
  ["Honest hourly ", "work", "."],
];

const pickPricingIndex = (slug) => {
  // Round-robin index across SERVICES_FLAT ensures 2-3 pages per heading
  // (21 services / 10 headings) with no random clustering from hashing.
  const idx = SERVICES_FLAT.findIndex((s) => s.slug === slug);
  return (idx >= 0 ? idx : 0) % PRICING_HEADINGS.length;
};

export default function ServiceDetail() {
  const { slug } = useParams();

  const { service, group, related, closer, heroPhoto, pricingHeading } = useMemo(() => {
    const svc = SERVICES_FLAT.find((s) => s.slug === slug);
    if (!svc) return { service: null, group: null, related: [], closer: "", heroPhoto: null, pricingHeading: null };
    const grp = SERVICE_GROUPS.find((g) => g.category === svc.category);
    const rel = grp ? grp.items.filter((i) => i.slug !== svc.slug).slice(0, 4) : [];
    const closerText = svc.closer || CLOSER_VARIANTS[pickCloserIndex(svc.slug)](svc, grp);
    const photo = SERVICE_PHOTOS[svc.slug] || null;
    const heading = PRICING_HEADINGS[pickPricingIndex(svc.slug)];
    return { service: svc, group: grp, related: rel, closer: closerText, heroPhoto: photo, pricingHeading: heading };
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (service) {
      document.title = `${service.title} | ${service.category}, Mobile Maintenance`;
      const desc = service.desc;
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.name = "description";
        document.head.appendChild(meta);
      }
      meta.setAttribute("content", desc);

      // JSON-LD Service schema
      const ld = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: service.title,
        serviceType: service.category,
        description: service.desc,
        provider: {
          "@type": "LocalBusiness",
          name: "Mobile Maintenance",
          telephone: "+1-432-276-7729",
          email: CONTACT.email,
          address: {
            "@type": "PostalAddress",
            streetAddress: "4041 S County Rd W",
            addressLocality: "Odessa",
            addressRegion: "TX",
            postalCode: "79766",
            addressCountry: "US",
          },
        },
        areaServed: "United States",
        ...(service.pricing && service.pricing.length && {
          offers: service.pricing.map((p) => ({
            "@type": "Offer",
            name: p.label,
            price: p.rate,
            priceCurrency: p.rate.startsWith("$") ? "USD" : undefined,
            description: `${p.rate} ${p.unit}`.trim(),
          })),
        }),
      };
      document.getElementById("ld-service")?.remove();
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "ld-service";
      s.text = JSON.stringify(ld);
      document.head.appendChild(s);
    }
    return () => document.getElementById("ld-service")?.remove();
  }, [service]);

  if (!service) {
    return (
      <>
        <Navbar />
        <main className="bg-mm-base pt-40 pb-32 min-h-screen text-center px-5" data-testid="service-not-found">
          <h1 className="font-heading font-black uppercase text-5xl text-white">Service Not Found</h1>
          <Link
            to="/#services"
            className="mt-8 inline-block text-mm-green font-mono uppercase tracking-widest"
            data-testid="back-to-services"
          >
            Back to Services
          </Link>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="bg-mm-base pt-28 sm:pt-32" data-testid="service-detail-page">
        {/* Hero */}
        <section
          className="relative overflow-hidden border-b border-mm-green/15 min-h-[560px] flex items-end bg-mm-base"
          data-testid="service-hero"
        >
          {/* Photo backdrop (only when mapped) */}
          {heroPhoto && (
            <motion.div
              initial={{ scale: 1.08, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 pointer-events-none"
            >
              <img
                src={heroPhoto.src}
                alt={`${service.title}, Mobile Maintenance`}
                className="w-full h-full object-cover"
                style={{ filter: "contrast(1.05)" }}
              />
              {heroPhoto.dark ? (
                <>
                  {/* Naturally-dark photo: minimal overlay so photo shows through */}
                  <div className="absolute inset-0" style={{ backgroundColor: "rgba(5,5,5,0.15)" }} />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-mm-base/20 to-mm-base/80" />
                  <div className="absolute inset-0 bg-gradient-to-r from-mm-base/50 via-transparent to-transparent" />
                </>
              ) : (
                <>
                  {/* Bright photo: stronger overlay so white title stays legible */}
                  <div className="absolute inset-0" style={{ backgroundColor: "rgba(5,5,5,0.35)" }} />
                  <div className="absolute inset-0 bg-gradient-to-b from-mm-base/40 via-mm-base/20 to-mm-base/90" />
                  <div className="absolute inset-0 bg-gradient-to-r from-mm-base/70 via-mm-base/20 to-transparent" />
                </>
              )}
            </motion.div>
          )}

          {/* Grid backdrop (always visible, subtle) */}
          <div
            className="absolute inset-0 pointer-events-none opacity-[0.07]"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(0,177,64,0.4) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,177,64,0.4) 1px, transparent 1px)",
              backgroundSize: "80px 80px",
            }}
          />

          <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-16 sm:py-24 relative w-full">
            {/* Breadcrumb */}
            <nav
              className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-gray-400 mb-10"
              data-testid="breadcrumb"
            >
              <Link to="/" className="hover:text-mm-green transition-colors">Home</Link>
              <ChevronRight size={12} />
              <Link to="/#services" className="hover:text-mm-green transition-colors">Services</Link>
              <ChevronRight size={12} />
              <span className="text-mm-green">{service.category}</span>
              <ChevronRight size={12} />
              <span className="text-white truncate">{service.title}</span>
            </nav>

            <div className="grid grid-cols-12 gap-8 items-end">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="col-span-12 lg:col-span-9"
              >
                <div className="font-mono text-xs uppercase tracking-[0.3em] text-white mb-5">
                  {service.category} · {group?.tag}
                </div>
                <h1 className="font-heading font-black uppercase tracking-tight text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.9]">
                  {service.title}
                </h1>
              </motion.div>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="col-span-12 lg:col-span-3 lg:text-right"
              >
                <a
                  href="/#request"
                  data-testid="cta-request-hero"
                  className="inline-flex items-center gap-2 font-heading font-bold uppercase tracking-wider bg-mm-green text-black px-6 py-3.5 hover:bg-white transition-colors"
                >
                  Get A Quote <ArrowRight size={18} />
                </a>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Description + Benefits */}
        <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 sm:py-28">
          <div className="grid grid-cols-12 gap-10 lg:gap-16">
            {/* Description */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="col-span-12 lg:col-span-7"
            >
              <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-mm-green mb-4">
                01 · Description
              </div>
              <h2 className="font-heading font-black uppercase text-white text-3xl sm:text-4xl leading-tight mb-6">
                What it is
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed" data-testid="service-description">
                {service.desc}
              </p>

              <div className="mt-10 h-px bg-mm-green/25 w-24" />
              <p className="mt-8 text-gray-400 leading-relaxed">{closer}</p>
            </motion.div>

            {/* Benefits */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="col-span-12 lg:col-span-5"
            >
              <div className="border border-mm-green/25 bg-mm-surface p-8 sm:p-10 relative">
                <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-mm-green mb-4">
                  02 · Benefits
                </div>
                <h2 className="font-heading font-black uppercase text-white text-3xl sm:text-4xl leading-tight mb-8">
                  What you get
                </h2>
                <ul className="space-y-5" data-testid="service-benefits">
                  {service.benefits.map((b, i) => (
                    <motion.li
                      key={b}
                      initial={{ opacity: 0, x: -10 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: i * 0.08 }}
                      className="flex items-start gap-4"
                    >
                      <span className="shrink-0 mt-0.5 w-6 h-6 border border-mm-green text-mm-green flex items-center justify-center">
                        <Check size={14} strokeWidth={2.5} />
                      </span>
                      <span className="text-white text-base sm:text-lg leading-snug">{b}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Pricing */}
        {service.pricing && service.pricing.length > 0 && (
          <section className="border-t border-mm-green/15">
            <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 sm:py-24">
              <div className="grid grid-cols-12 gap-10 lg:gap-16 items-start">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="col-span-12 lg:col-span-4"
                >
                  <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-mm-green mb-4">
                    03 · Pricing
                  </div>
                  <h2 className="font-heading font-black uppercase text-white text-3xl sm:text-4xl leading-tight">
                    {pricingHeading[0]}
                    <span className="text-mm-green">{pricingHeading[1]}</span>
                    {pricingHeading[2]}
                  </h2>
                  <p className="mt-5 text-sm text-gray-400 leading-relaxed max-w-sm">
                    {PRICING_DISCLAIMER}
                  </p>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  className="col-span-12 lg:col-span-8"
                >
                  <div className="border border-mm-green/25" data-testid="service-pricing">
                    {service.pricing.map((p, i) => (
                      <div
                        key={p.label}
                        className={`grid grid-cols-12 items-center gap-4 px-6 sm:px-8 py-6 ${
                          i !== service.pricing.length - 1 ? "border-b border-mm-green/15" : ""
                        } hover:bg-mm-surface transition-colors`}
                      >
                        <div className="col-span-12 sm:col-span-8">
                          <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-mm-green/80 mb-1">
                            Line {String(i + 1).padStart(2, "0")}
                          </div>
                          <div className="text-white text-base sm:text-lg leading-snug">
                            {p.label}
                          </div>
                        </div>
                        <div className="col-span-12 sm:col-span-4 sm:text-right">
                          <span className="font-heading font-black uppercase text-mm-green text-3xl sm:text-4xl tracking-tight leading-none">
                            {p.rate}
                          </span>
                          {p.unit && (
                            <span className="ml-1 font-mono text-xs uppercase tracking-widest text-gray-400">
                              {p.unit}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </section>
        )}

        {/* CTA band */}
        <section className="bg-mm-panel border-y border-mm-green/20">
          <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-14 sm:py-16">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div>
                <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-mm-green mb-2">
                  24 / 7 / 365 · Nationwide
                </div>
                <h3 className="font-heading font-black uppercase text-white text-3xl sm:text-4xl leading-tight">
                  Need {service.title.toLowerCase()}? <span className="text-mm-green">Let's talk.</span>
                </h3>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={CONTACT.phoneHref}
                  data-testid="cta-call"
                  className="inline-flex items-center justify-center gap-2 font-heading font-bold uppercase tracking-wider border border-mm-green/40 text-white px-6 py-3.5 hover:border-mm-green hover:text-mm-green transition-colors"
                >
                  Call {CONTACT.phone}
                </a>
                <a
                  href="/#request"
                  data-testid="cta-request-service"
                  className="inline-flex items-center justify-center gap-2 font-heading font-bold uppercase tracking-wider bg-mm-green text-black px-6 py-3.5 hover:bg-white transition-colors"
                >
                  Get A Quote <ArrowRight size={18} />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Related services */}
        {related.length > 0 && (
          <section className="mx-auto max-w-[1400px] px-5 sm:px-8 py-20 sm:py-28">
            <div className="flex items-end justify-between mb-10 border-b border-mm-green/20 pb-6">
              <div>
                <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-mm-green mb-2">
                  More In {group?.category}
                </div>
                <h3 className="font-heading font-black uppercase text-white text-3xl sm:text-4xl">
                  Related Services
                </h3>
              </div>
              <Link
                to="/#services"
                className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-gray-400 hover:text-mm-green transition-colors"
                data-testid="view-all-services"
              >
                All Services <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-mm-green/15 border border-mm-green/15">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  data-testid={`related-service-${r.slug}`}
                  className="group block bg-mm-surface hover:bg-mm-panel transition-colors duration-300 p-6 min-h-[180px]"
                >
                  <div className="flex justify-end mb-6">
                    <ArrowRight
                      size={18}
                      className="text-gray-600 group-hover:text-mm-green group-hover:translate-x-1 transition-all"
                    />
                  </div>
                  <h4 className="font-heading font-bold uppercase text-lg text-white leading-tight group-hover:text-mm-green transition-colors">
                    {r.title}
                  </h4>
                  <p className="mt-2 text-xs text-gray-400 line-clamp-2">{r.desc}</p>
                </Link>
              ))}
              {/* Fill empty tail cells so the grid never shows a hollow gap */}
              {Array.from({ length: (4 - (related.length % 4)) % 4 }).map((_, k) => (
                <div
                  key={`related-filler-${k}`}
                  aria-hidden="true"
                  className="hidden lg:block bg-mm-surface min-h-[180px]"
                />
              ))}
            </div>
          </section>
        )}

        {/* Back link */}
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 pb-24">
          <Link
            to="/#services"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-mm-green transition-colors font-mono text-xs uppercase tracking-widest"
            data-testid="back-to-all-services"
          >
            <ArrowLeft size={14} /> Back to All Services
          </Link>
        </div>
      </main>
      <Footer />
    </>
  );
}
